import {
  Lead,
  Listing,
  TourSlot,
  Application,
  Lease,
  OnboardingChecklist,
  LeaseRenewalTerms,
  RenewalOfferTerms,
  TenantLeasePortalJourney,
  Tenant,
} from '@repo/shared-types';
import type { Note } from '@repo/shared-types/common/notes';
import { apiTransport as router } from './transport';
import type { QueryFilters } from './transport';
import { getContext } from './core';

// Listing by id is persisted GET; addendum POST, onboarding subroutes: not all wired.
// GET /leasing/leases/by-application/:id returns Lease[] (newest first). DELETE /leasing/applications/:id redacts PII (Fair Housing); other guarded DELETEs may still return 501.
export const leasingApi = {
  leads: {
    list: async () => router.get<Lead[]>('/leasing/leads'),
    get: async (id: string) => router.get<Lead>(`/leasing/leads/${id}`),
    create: async (lead: Lead) => {
      const context = getContext();
      return router.post<Lead>('/leasing/leads', lead, context);
    },
    update: async (id: string, updates: Partial<Lead>) => {
      const context = getContext();
      return router.patch<Lead>(`/leasing/leads/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/leasing/leads/${id}`, context);
    },
    submitQuestionnaire: async (id: string, answers: Record<string, string>) => {
      return router.post<Lead>(`/leasing/leads/${id}/questionnaire`, { answers });
    },
  },
  publicTours: {
    listSlots: async (params?: { propertyId?: string; from?: string; to?: string }) => {
      const q = new URLSearchParams();
      if (params?.propertyId) q.set('propertyId', params.propertyId);
      if (params?.from) q.set('from', params.from);
      if (params?.to) q.set('to', params.to);
      const path = q.toString() ? `/public/tours/slots?${q.toString()}` : '/public/tours/slots';
      return router.get<TourSlot[]>(path);
    },
    bookSlot: async (slotId: string, body: { name: string; email: string; phone: string; unitId?: string; floorPlanId?: string }) => {
      return router.post<{ leadId: string; tourSlotId: string; confirmationMessage: string }>(`/public/tours/slots/${slotId}/book`, body);
    },
  },
  publicMarketing: {
    listProperties: async () =>
      router.get<Array<{ id: string; name: string; city: string | null }>>('/public/properties'),
    listFloorPlans: async (propertyId: string) =>
      router.get<Array<{ id: string; name: string; bed: number | null; bath: number | null }>>(
        `/public/properties/${propertyId}/floorplans`,
      ),
    listUnits: async (propertyId: string) =>
      router.get<Array<{ id: string; unitNumber: string | null; floorPlanId: string }>>(
        `/public/properties/${propertyId}/units`,
      ),
  },
  applications: {
    list: async (filters?: Record<string, string>) => {
      const context = getContext();
      return router.get<Application[]>('/leasing/applications', context, Object.keys(filters ?? {}).length ? filters : undefined);
    },
    get: async (id: string) => router.get<Application>(`/leasing/applications/${id}`),
    create: async (application: Application) => {
      const context = getContext();
      return router.post<Application>('/leasing/applications', application, context);
    },
    update: async (id: string, updates: Partial<Application>) => {
      const context = getContext();
      return router.patch<Application>(`/leasing/applications/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<Application>(`/leasing/applications/${id}`, context);
    },
    notes: {
      list: async (applicationId: string) => router.get<Note[]>(`/leasing/applications/${applicationId}/notes`),
      create: async (note: { applicationId: string; content: string; category?: string; isImportant?: boolean }) => {
        const context = getContext();
        return router.post<Note>(`/leasing/applications/${note.applicationId}/notes`, note, context);
      },
      update: async (id: string, updates: Partial<Pick<Note, 'content' | 'category' | 'isImportant'>>) =>
        router.patch<Note>(`/leasing/applications/notes/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/leasing/applications/notes/${id}`)
    },
    getBackgroundCheckStatus: async (applicationId: string) => {
      return router.get<{ status: string; reportUrl?: string }>(`/leasing/applications/${applicationId}/background-check`);
    },
    requestBackgroundCheck: async (applicationId: string, provider: string = 'checkr') => {
      return router.post<{ success: boolean; requestId: string }>(`/leasing/applications/${applicationId}/background-check`, { provider });
    },
    generateLease: async (id: string) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/applications/${id}/generate-lease`, {}, context);
    }
  },
  listings: {
    list: async (filters?: { propertyId?: string }) => {
      const q = filters?.propertyId ? `?propertyId=${encodeURIComponent(filters.propertyId)}` : '';
      return router.get<Listing[]>(`/leasing/listings${q}`);
    },
    get: async (id: string) => router.get<Listing>(`/leasing/listings/${id}`),
    create: async (listing: Listing) => {
      const context = getContext();
      return router.post<Listing>('/leasing/listings', listing, context);
    },
    update: async (id: string, updates: Partial<Listing>) => {
      const context = getContext();
      return router.patch<Listing>(`/leasing/listings/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/leasing/listings/${id}`, context);
    }
  },
  tours: {
    list: async () => router.get<TourSlot[]>('/leasing/tours'),
    get: async (id: string) => router.get<TourSlot>(`/leasing/tours/${id}`),
    create: async (slot: Omit<TourSlot, 'id'>) => {
      const context = getContext();
      return router.post<TourSlot>('/leasing/tours', slot, context);
    },
    update: async (id: string, updates: Partial<TourSlot>) => {
      const context = getContext();
      return router.patch<TourSlot>(`/leasing/tours/${id}`, updates, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/leasing/tours/${id}`, context);
    },
    assignLeads: async (slotId: string, leadIds: string[]) => {
      const context = getContext();
      return router.post<TourSlot>(`/leasing/tours/${slotId}/leads`, { leadIds }, context);
    },
  },
  leases: {
    list: async (filters?: QueryFilters) => {
      const context = getContext();
      return router.get<Lease[] | { data: Lease[]; total: number }>('/leasing/leases', context, Object.keys((filters ?? {}) as Record<string, string>).length ? filters : undefined);
    },
    get: async (id: string) => {
      const context = getContext();
      return router.get<Lease>(`/leasing/leases/${id}`, context);
    },
    /** Newest lease first; empty array if none. */
    getByApplication: async (applicationId: string) => {
      const context = getContext();
      return router.get<Lease[]>(`/leasing/leases/by-application/${applicationId}`, context);
    },
    getPortalJourney: async () => {
      const context = getContext();
      return router.get<TenantLeasePortalJourney>('/leasing/leases/portal-journey', context);
    },
    generateFromApplication: async (applicationId: string) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/applications/${applicationId}/generate-lease`, {}, context);
    },
    create: async (lease: Omit<Lease, 'id' | 'createdAt' | 'updatedAt'>) => {
      const context = getContext();
      return router.post<Lease>('/leasing/leases', lease, context);
    },
    update: async (id: string, updates: Partial<Lease>) => {
      const context = getContext();
      return router.patch<Lease>(`/leasing/leases/${id}`, updates, context);
    },
    delete: async (id: string) => router.delete<{ success: boolean; lease: Lease }>(`/leasing/leases/${id}`),
    sendForSignature: async (id: string, tenantEmail?: string) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/leases/${id}/send-for-signature`, { tenantEmail }, context);
    },
    sign: async (id: string, signatureData?: { type: 'tenant' | 'landlord'; signature: string; signedBy?: string }) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/leases/${id}/sign`, signatureData, context);
    },
    activate: async (id: string) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/leases/${id}/activate`, {}, context);
    },
    renew: async (id: string, terms: LeaseRenewalTerms) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/leases/${id}/renew`, terms, context);
    },
    terminate: async (
      id: string,
      body: { noticeDate: string; terminationDate: string; reason?: string; notes?: string } = {
        noticeDate: new Date().toISOString().slice(0, 10),
        terminationDate: new Date().toISOString().slice(0, 10),
      },
    ) => {
      return router.post<Lease>(`/leasing/leases/${id}/terminate`, body);
    },
    createAddendum: async (id: string, addendum: Omit<NonNullable<Lease['addendums']>[number], 'id' | 'createdAt'>) => {
      const context = getContext();
      return router.post<Lease>(`/leasing/leases/${id}/addendum`, addendum, context);
    },
    renewals: {
      list: async (leaseId?: string) => router.get<import('@repo/shared-types/leasing/schemas').RenewalOffer[]>(`/leasing/renewals${leaseId ? `?leaseId=${leaseId}` : ''}`),
      get: async (id: string) => router.get<import('@repo/shared-types/leasing/schemas').RenewalOffer>(`/leasing/renewals/${id}`),
      create: async (leaseId: string, renewal: RenewalOfferTerms) =>
        router.post<import('@repo/shared-types/leasing/schemas').RenewalOffer>('/leasing/renewals', { ...renewal, leaseId }),
      send: async (id: string) => router.post<{ success: boolean }>(`/leasing/renewals/${id}/send`, {}),
      update: async (id: string, updates: Partial<RenewalOfferTerms>) =>
        router.patch<import('@repo/shared-types/leasing/schemas').RenewalOffer>(`/leasing/renewals/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/leasing/renewals/${id}`)
    }
  },
  onboarding: {
    checklists: {
      list: async () => router.get<OnboardingChecklist[]>('/leasing/onboarding/checklists'),
      getByTenant: async (tenantId: string) => router.get<OnboardingChecklist>(`/leasing/onboarding/checklists/tenant/${tenantId}`),
      getByApplication: async (applicationId: string) => router.get<OnboardingChecklist>(`/leasing/onboarding/checklists/application/${applicationId}`),
      create: async (checklist: Omit<OnboardingChecklist, 'id' | 'createdAt' | 'updatedAt'>) => router.post<OnboardingChecklist>('/leasing/onboarding/checklists', checklist),
      update: async (id: string, updates: Partial<OnboardingChecklist>) => router.patch<OnboardingChecklist>(`/leasing/onboarding/checklists/${id}`, updates),
      sendReminder: async (id: string) => router.post<{ success: boolean }>(`/leasing/onboarding/checklists/${id}/reminder`, {}),
      confirmUtility: async (checklistId: string, itemId: string, confirmationNumber?: string) =>
        router.post<OnboardingChecklist>(`/leasing/onboarding/checklists/${checklistId}/items/${itemId}/confirm-utility`, { confirmationNumber }),
      scheduleInspection: async (checklistId: string, inspectionDate: string, notes?: string) =>
        router.post<OnboardingChecklist>(`/leasing/onboarding/checklists/${checklistId}/schedule-inspection`, { inspectionDate, notes }),
      items: {
        add: async (checklistId: string, item: Omit<import('@repo/shared-types/leasing/onboarding').OnboardingChecklistItem, 'id'>) => router.post<OnboardingChecklist>(`/leasing/onboarding/checklists/${checklistId}/items`, item),
        update: async (checklistId: string, itemId: string, updates: Partial<import('@repo/shared-types/leasing/onboarding').OnboardingChecklistItem>) => router.patch<OnboardingChecklist>(`/leasing/onboarding/checklists/${checklistId}/items/${itemId}`, updates),
        delete: async (checklistId: string, itemId: string) => router.delete<{ success: boolean }>(`/leasing/onboarding/checklists/${checklistId}/items/${itemId}`)
      }
    }
  },
  depositSettlements: {
    getPdf: async (settlementId: string) => {
      const context = getContext();
      return router.get<{ fileName: string; contentType: 'application/pdf'; base64: string }>(
        `/leasing/deposit-settlements/${encodeURIComponent(settlementId)}/pdf`,
        context,
      );
    },
    getPreviewPdf: async (settlementId: string) => {
      const context = getContext();
      return router.get<{ fileName: string; contentType: 'application/pdf'; base64: string }>(
        `/leasing/deposit-settlements/${encodeURIComponent(settlementId)}/preview-pdf`,
        context,
      );
    },
  },
  docusign: {
    isConfigured: async () => {
      const res = await router.get<{ configured: boolean }>('/leasing/docusign/configured', getContext());
      return res?.configured ?? false;
    },
    getEnvelopeStatus: async (envelopeId: string) => {
      return router.get<import('@repo/shared-types/docusign').DocuSignEnvelope>(
        `/leasing/docusign/envelopes/${envelopeId}/status`,
        getContext()
      );
    }
  },
  /** PM lease workflows — scoped resident directory (GET /leasing/tenants). */
  residents: {
    list: async (filters?: Record<string, string>) => {
      const context = getContext();
      return router.get<Tenant[] | { data: Tenant[]; total: number }>(
        '/leasing/tenants',
        context,
        Object.keys(filters ?? {}).length ? filters : undefined,
      );
    },
  },
};

